import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../../../firebase";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { statusFields } from "../../../../utils/fields";
import AdminPage from "../../../core/admin/adminPage";
import AdminCard from "../../../core/admin/adminCard";
import { AdminLoading } from "../../../core/admin/adminSpinner";
import AdminBadge, { WatchStatusBadge } from "../../../core/admin/adminBadge";
import { ArrowRightIcon, StarLineIcon } from "../../../../img/svg/adminIcons";

export default function AdminGeneral() {

    const [statuses, setStatuses] = useState<{ [key: string]: number }>({});
    const [users, setUsers] = useState<{ [key: string]: { label: string, count: number } }>({});
    const [requestsCount, setRequestsCount] = useState(0);
    const [watchesLoading, setWatchesLoading] = useState(false);
    const [requestsLoading, setRequestsLoading] = useState(false);
    const [usersLoading, setUsersLoading] = useState(false);

    useEffect(() => {
        countWatchesStatus();
        countRequests();
        countUsers();
    }, []);

    const countWatchesStatus = async () => {
        try {
            setWatchesLoading(true);
            const statusCounts: { [key: string]: any } = {};
            const q = collection(db, "watches");
            const docsSnap = await getDocs(q);
            if (docsSnap.empty) {
                setWatchesLoading(false);
                return null;
            }
            docsSnap.forEach(document => {
                const d = document.data();
                const status = d.status;
                if (statusCounts[status]) {
                    statusCounts[status]++;
                } else {
                    statusCounts[status] = 1;
                }
            })

            setStatuses(statusCounts);

        } catch (error) {
            console.log(error);
            return null;
        }
        finally {
            setWatchesLoading(false);
        }
    }

    const countRequests = async () => {
        try {
            setRequestsLoading(true);
            const q = query(collection(db, "reviewsrequests"), where("solved", "==", false));
            const docsSnap = await getDocs(q);
            if (docsSnap.empty) {
                setRequestsLoading(false);
                return null;
            }
            setRequestsCount(docsSnap.size);
        } catch (error) {
            console.log(error);
        } finally {
            setRequestsLoading(false);
        }
    }

    const countUsers = async () => {
        try {
            setUsersLoading(true);
            let admins = 0;
            let advertisers = 0;
            let customers = 0;
            const q = collection(db, "users");
            const docsSnap = await getDocs(q);
            if (docsSnap.empty) {
                setUsersLoading(false);
                return null;
            }
            docsSnap.forEach(document => {
                const d = document.data();
                if (d.roles.includes("admin")) {
                    admins++;
                }
                if (d.roles.includes("advertiser")) {
                    advertisers++;
                }
                if (d.roles.includes("customer")) {
                    customers++;
                }
            })

            setUsers({
                admins: { label: "Administradores", count: admins },
                advertisers: { label: "Anunciantes", count: advertisers },
                customers: { label: "Clientes", count: customers }
            })

        } catch (error) {
            console.log(error);
            return null;
        }
        finally {
            setUsersLoading(false);
        }
    }

    const totalWatches = Object.keys(statuses).reduce((acc, k) => acc + (statuses[k] || 0), 0);

    return (
        <AdminPage title="Geral" description="Números gerais da plataforma, calculados no momento em que a tela é aberta.">

            {/* SOLICITAÇÕES */}
            {
                requestsLoading ?
                    <AdminCard><AdminLoading className="!py-4" /></AdminCard>
                    :
                    <Link to="/admin/reviews" className={`group flex items-center gap-4 rounded-xl border p-4 md:p-5 shadow-sm transition hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary ${requestsCount > 0 ? "border-red-200 bg-red-50" : "border-gray-200 bg-white"}`}>
                        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${requestsCount > 0 ? "bg-red-500 text-white" : "bg-gray-900 text-white"}`}>
                            <StarLineIcon />
                        </span>
                        <div className="min-w-0 flex-1">
                            <p className="font-bold text-gray-900">Solicitações sobre avaliações</p>
                            <p className="text-sm text-gray-500">{requestsCount > 0 ? "Há pedidos de exclusão aguardando análise." : "Nenhum pedido de exclusão pendente."}</p>
                        </div>
                        <span className={`text-3xl font-bold tabular-nums ${requestsCount > 0 ? "text-red-600" : "text-gray-900"}`}>{requestsCount}</span>
                        <span className="hidden sm:block text-gray-400 transition group-hover:translate-x-0.5 group-hover:text-gray-900"><ArrowRightIcon width="18px" /></span>
                    </Link>
            }

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* RELÓGIOS */}
                <AdminCard
                    title="Relógios"
                    description={watchesLoading ? undefined : `${totalWatches} cadastrados no total`}
                    actions={<Link to="/admin/watches" className="text-sm font-semibold text-gray-600 hover:text-gray-900 inline-flex items-center gap-1">Ver relógios <ArrowRightIcon width="14px" /></Link>}
                    bodyClassName="p-2"
                >
                    {
                        watchesLoading ?
                            <AdminLoading />
                            :
                            <ul className="divide-y divide-gray-100">
                                {
                                    Object.keys(statusFields).map((f, i) => (
                                        <li key={i} className={`flex items-center justify-between gap-4 rounded-lg px-3 py-3 ${f === "approval" && statuses[f] > 0 ? "bg-amber-50" : ""}`}>
                                            <span className="flex items-center gap-2">
                                                <WatchStatusBadge status={f} />
                                                {
                                                    f === "approval" && statuses[f] > 0 ?
                                                        <span className="text-xs font-semibold text-amber-700">requer atenção</span>
                                                        : null
                                                }
                                            </span>
                                            <span className="text-lg font-bold tabular-nums text-gray-900">{statuses[f] || 0}</span>
                                        </li>
                                    ))
                                }
                            </ul>
                    }
                </AdminCard>

                {/* USUÁRIOS */}
                <AdminCard
                    title="Usuários"
                    description="Um mesmo usuário pode ter mais de um papel."
                    actions={<Link to="/admin/users" className="text-sm font-semibold text-gray-600 hover:text-gray-900 inline-flex items-center gap-1">Ver usuários <ArrowRightIcon width="14px" /></Link>}
                    bodyClassName="p-2"
                >
                    {
                        usersLoading ?
                            <AdminLoading />
                            :
                            <ul className="divide-y divide-gray-100">
                                {
                                    Object.keys(users).map((u, i) => (
                                        <li key={i} className="flex items-center justify-between gap-4 px-3 py-3">
                                            <AdminBadge tone={u === "admins" ? "dark" : u === "advertisers" ? "gold" : "neutral"}>{users[u].label}</AdminBadge>
                                            <span className="text-lg font-bold tabular-nums text-gray-900">{users[u].count}</span>
                                        </li>
                                    ))
                                }
                            </ul>
                    }
                </AdminCard>
            </div>
        </AdminPage>
    )
}
